(function () {
  'use strict';

  var MIN_LENGTH = 2;
  var DEBOUNCE_MS = 250;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.suggestions = this.querySelector('[data-search-suggestions]');
      this.form = this.querySelector('form');
      this.url = this.getAttribute('data-url');
      this.timer = null;
      this.controller = null;
      this.activeIndex = -1;
      this.dialog = this.closest('dialog');

      if (!this.input || !this.results) return;

      this.results.setAttribute('role', 'listbox');
      this.results.setAttribute('aria-label', (window.byju && window.byju.strings && window.byju.strings.searchResults) || '');
      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      if (this.dialog) {
        this.dialog.addEventListener('drawer:open', this.onOpen.bind(this));
        this.dialog.addEventListener('drawer:close', this.reset.bind(this));
      }
    }

    onOpen() {
      this.input.focus();
    }

    onInput() {
      window.clearTimeout(this.timer);
      var term = this.input.value.trim();
      if (term.length < MIN_LENGTH) {
        this.abort();
        this.clearResults();
        return;
      }
      this.timer = window.setTimeout(this.search.bind(this, term), DEBOUNCE_MS);
    }

    abort() {
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    search(term) {
      this.abort();
      this.controller = new AbortController();
      var controller = this.controller;
      var params = new URLSearchParams({ q: term, section_id: 'predictive-search' });
      params.set('resources[type]', 'product,collection,page,article');
      params.set('resources[limit]', '4');
      params.set('resources[options][unavailable_products]', 'last');

      this.results.setAttribute('aria-busy', 'true');
      this.setLoading(true);

      fetch(this.url + '?' + params.toString(), { signal: controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error(String(response.status));
          return response.text();
        })
        .then(
          function (html) {
            if (controller !== this.controller) return;
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var root = doc.querySelector('[data-predictive-root]');
            this.results.replaceChildren();
            if (root) {
              this.results.appendChild(root);
              this.setStatus(root.getAttribute('data-status'));
            }
            this.toggleSuggestions(!root);
            this.activeIndex = -1;
            this.input.setAttribute('aria-expanded', root ? 'true' : 'false');
            this.input.removeAttribute('aria-activedescendant');
          }.bind(this)
        )
        .catch(
          function (error) {
            if (error.name === 'AbortError') return;
            var message = document.createElement('p');
            message.className = 'psearch__empty';
            message.textContent = (window.byju && window.byju.strings && window.byju.strings.searchError) || '';
            this.results.replaceChildren(message);
            this.toggleSuggestions(true);
          }.bind(this)
        )
        .finally(
          function () {
            if (controller !== this.controller) return;
            this.results.removeAttribute('aria-busy');
          }.bind(this)
        );
    }

    setLoading(loading) {
      if (!loading) return;
      var text = (window.byju && window.byju.strings && window.byju.strings.loading) || '';
      this.setStatus(text);
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text || '';
    }

    toggleSuggestions(show) {
      if (this.suggestions) this.suggestions.hidden = !show;
    }

    clearResults() {
      this.results.replaceChildren();
      this.toggleSuggestions(true);
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.setStatus('');
    }

    reset() {
      window.clearTimeout(this.timer);
      this.abort();
      this.input.value = '';
      this.clearResults();
    }

    options() {
      return Array.prototype.slice.call(this.results.querySelectorAll('[role="option"]'));
    }

    setActive(index) {
      var options = this.options();
      if (!options.length) return;
      if (index < 0) index = options.length - 1;
      if (index >= options.length) index = 0;
      options.forEach(function (option, i) {
        option.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      this.activeIndex = index;
      this.input.setAttribute('aria-activedescendant', options[index].id);
      options[index].scrollIntoView({ block: 'nearest' });
    }

    onKeydown(event) {
      var options = this.options();
      switch (event.key) {
        case 'ArrowDown':
          if (options.length) {
            event.preventDefault();
            this.setActive(this.activeIndex + 1);
          }
          break;
        case 'ArrowUp':
          if (options.length) {
            event.preventDefault();
            this.setActive(this.activeIndex - 1);
          }
          break;
        case 'Home':
          if (options.length && this.activeIndex > -1) {
            event.preventDefault();
            this.setActive(0);
          }
          break;
        case 'End':
          if (options.length && this.activeIndex > -1) {
            event.preventDefault();
            this.setActive(options.length - 1);
          }
          break;
        case 'Escape':
          if (options.length || this.input.value) {
            event.preventDefault();
            this.reset();
          }
          break;
        default:
          break;
      }
    }

    onSubmit(event) {
      var options = this.options();
      if (this.activeIndex > -1 && options[this.activeIndex]) {
        event.preventDefault();
        window.location.href = options[this.activeIndex].getAttribute('href');
      }
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();
